import { createSerializer, parseAsString, parseAsStringLiteral, type UrlKeys } from 'nuqs';

import { LAST_4_SSN_LENGTH } from '@clearverify/constants';

import { EmployeeSearchInputKindEnum } from 'src/api';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';

import { toIsoDate, toSearchDigits, toSearchText } from './employeeSearch';

/** URL-facing names for the search kinds — `Personal_Info` & co. don't belong in a link. */
export const EMPLOYEE_SEARCH_URL_KINDS = [
  'identity',
  'employee-id',
  'phone',
  'email',
  'direct',
] as const;

export type EmployeeSearchUrlKind = (typeof EMPLOYEE_SEARCH_URL_KINDS)[number];

const URL_KIND_TO_CRITERIA_KIND = {
  identity: EmployeeSearchInputKindEnum.PersonalInfo,
  'employee-id': EmployeeSearchInputKindEnum.EmployeeId,
  phone: EmployeeSearchInputKindEnum.Phone,
  email: EmployeeSearchInputKindEnum.Email,
  direct: EmployeeSearchInputKindEnum.Direct,
} satisfies Record<EmployeeSearchUrlKind, EmployeeSearchInputKindEnum>;

const CRITERIA_KIND_TO_URL_KIND = {
  [EmployeeSearchInputKindEnum.PersonalInfo]: 'identity',
  [EmployeeSearchInputKindEnum.EmployeeId]: 'employee-id',
  [EmployeeSearchInputKindEnum.Phone]: 'phone',
  [EmployeeSearchInputKindEnum.Email]: 'email',
  [EmployeeSearchInputKindEnum.Direct]: 'direct',
} satisfies Record<EmployeeSearchInputKindEnum, EmployeeSearchUrlKind>;

/**
 * Criteria fields deliberately kept out of the URL. The full SSN is the reason
 * this list exists: a search URL is shared, bookmarked and logged, so it must
 * never carry one. Add a field here to stop persisting it — the parser map
 * below then rejects its parser, and `criteriaToUrlState` stops compiling until
 * the field is dropped from the projection too.
 *
 * `employeeId` stays out of this list on purpose: our own id is an opaque cuid
 * that identifies nobody on its own and resolves only for a caller RLS already
 * lets read that employee, so a direct link may carry it where an SSN may not.
 */
const NON_PERSISTED_CRITERIA_FIELDS = ['ssn'] as const;

type NonPersistedCriteriaField = (typeof NON_PERSISTED_CRITERIA_FIELDS)[number];

type PersistedCriteriaField = Exclude<
  keyof EmployeeSearchCriteria,
  'kind' | NonPersistedCriteriaField
>;

// Exhaustive both ways: every persisted criteria field needs a parser, and a
// parser for a non-persisted (or unknown) field is a type error.
const criteriaUrlParsers = {
  last4Ssn: parseAsString,
  firstName: parseAsString,
  lastName: parseAsString,
  birthDate: parseAsString,
  extEmployeeId: parseAsString,
  employeeId: parseAsString,
  phoneNumber: parseAsString,
  email: parseAsString,
} satisfies Record<PersistedCriteriaField, typeof parseAsString>;

export const employeeSearchUrlParsers = {
  kind: parseAsStringLiteral(EMPLOYEE_SEARCH_URL_KINDS),
  ...criteriaUrlParsers,
};

/** Shorter param names where the state key is clumsy in a URL. */
export const employeeSearchUrlKeys: UrlKeys<typeof employeeSearchUrlParsers> = {
  last4Ssn: 'last4',
  phoneNumber: 'phone',
  // The id the user types on the Employee ID tab is the employer's, so it keeps
  // the plain name; `id` is our own opaque `Employee.id` behind a direct link.
  extEmployeeId: 'employeeId',
  employeeId: 'id',
};

export type EmployeeSearchUrlState = {
  kind: EmployeeSearchUrlKind | null;
} & Record<PersistedCriteriaField, string | null>;

const EMPLOYEE_SEARCH_URL_STATE_KEYS = Object.keys(
  employeeSearchUrlParsers,
) as (keyof EmployeeSearchUrlState)[];

const toUrlValue = (value: string | null | undefined): string | null => value || null;

/**
 * Projects criteria onto the URL. Every key is always present — unused ones as
 * `null` — so applying the result clears params left over from another kind.
 */
export const criteriaToUrlState = (criteria: EmployeeSearchCriteria): EmployeeSearchUrlState => ({
  kind: CRITERIA_KIND_TO_URL_KIND[criteria.kind],
  last4Ssn: toUrlValue(criteria.last4Ssn),
  firstName: toUrlValue(criteria.firstName),
  lastName: toUrlValue(criteria.lastName),
  birthDate: toUrlValue(criteria.birthDate),
  extEmployeeId: toUrlValue(criteria.extEmployeeId),
  employeeId: toUrlValue(criteria.employeeId),
  phoneNumber: toUrlValue(criteria.phoneNumber),
  email: toUrlValue(criteria.email),
});

/**
 * Rebuilds criteria from the URL, reading only the fields that belong to the
 * stated kind and re-sanitizing them (the URL is user-editable). Returns `null`
 * when there's nothing to search for, which keeps the search queries skipped.
 */
export const urlStateToCriteria = (
  state: EmployeeSearchUrlState,
): EmployeeSearchCriteria | null => {
  if (!state.kind) return null;

  const kind = URL_KIND_TO_CRITERIA_KIND[state.kind];

  switch (kind) {
    case EmployeeSearchInputKindEnum.PersonalInfo: {
      const last4Ssn = toSearchDigits(state.last4Ssn);
      const criteria: EmployeeSearchCriteria = {
        kind,
        last4Ssn: last4Ssn?.length === LAST_4_SSN_LENGTH ? last4Ssn : undefined,
        firstName: toSearchText(state.firstName),
        lastName: toSearchText(state.lastName),
        birthDate: toIsoDate(state.birthDate),
      };
      // An identity search that only carried a (non-persisted) SSN restores to
      // nothing rather than to a broad name-less search.
      const hasValue = Object.entries(criteria).some(([key, value]) => key !== 'kind' && !!value);
      return hasValue ? criteria : null;
    }

    case EmployeeSearchInputKindEnum.EmployeeId: {
      const extEmployeeId = toSearchText(state.extEmployeeId);
      return extEmployeeId ? { kind, extEmployeeId } : null;
    }

    case EmployeeSearchInputKindEnum.Direct: {
      const employeeId = toSearchText(state.employeeId);
      return employeeId ? { kind, employeeId } : null;
    }

    case EmployeeSearchInputKindEnum.Phone: {
      const phoneNumber = toSearchText(state.phoneNumber);
      return phoneNumber ? { kind, phoneNumber } : null;
    }

    case EmployeeSearchInputKindEnum.Email: {
      const email = toSearchText(state.email);
      return email ? { kind, email } : null;
    }
  }
};

/**
 * Whether these criteria address one known employee rather than describe a
 * search. The search itself runs exactly as it does for any other kind; only
 * the UI differs, since there's no query for the user to refine.
 */
export const isDirectEmployeeCriteria = (criteria: EmployeeSearchCriteria | null): boolean =>
  criteria?.kind === EmployeeSearchInputKindEnum.Direct;

export const isSameEmployeeSearchUrlState = (
  a: EmployeeSearchUrlState,
  b: EmployeeSearchUrlState,
): boolean => EMPLOYEE_SEARCH_URL_STATE_KEYS.every(key => (a[key] ?? null) === (b[key] ?? null));

/**
 * Builds a link into the employee search. Sharing the parsers with the hook is
 * the point — the param names can't drift apart from what the screen reads.
 */
const serializeEmployeeSearchUrl = createSerializer(employeeSearchUrlParsers, {
  urlKeys: employeeSearchUrlKeys,
});

/** A link that opens the search screen on one employee. */
export const buildDirectEmployeeUrl = (path: string, employeeId: string): string =>
  serializeEmployeeSearchUrl(path, { kind: 'direct', employeeId });
